import React from 'react';
import MainLayout from '../components/layout/MainLayout';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import { motion } from 'framer-motion';

const ServiceCard = ({ 
  title, 
  problem, 
  duration, 
  price, 
  description, 
  pointsTitle,
  points = [], 
  extraSections = [],
  deliverables = [], 
  deliverablesTitle = "What you leave with:",
  outcome, 
  linkText, 
  href, 
  to, 
  variant = "outline" 
}) => (
  <div className={`bg-white p-8 md:p-10 rounded-[2.5rem] shadow-sm border ${variant === 'primary' ? 'border-brand-teal ring-1 ring-brand-teal/20 shadow-lg' : 'border-gray-100'} flex flex-col h-full transition-all hover:shadow-md`}>
    <div className="mb-8">
      <h3 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight mb-4">{title}</h3>
      <div className="bg-brand-cream p-5 rounded-2xl border border-gray-100">
        <p className="text-brand-navy font-semibold italic text-sm leading-relaxed">"{problem}"</p>
      </div>
    </div>

    <div className="mb-8 flex flex-wrap gap-3">
      {duration && (
        <span className="bg-brand-teal/10 text-brand-teal px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider">
          {duration}
        </span>
      )}
      {price && (
        <span className="bg-brand-navy/5 text-brand-navy px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-wider">
          {price}
        </span>
      )}
    </div>

    <div className="text-gray-700 mb-8 text-sm lg:text-base leading-relaxed space-y-4">
      {Array.isArray(description) ? description.map((p, i) => <p key={i}>{p}</p>) : <p>{description}</p>}
    </div>

    <div className="space-y-8 flex-grow">
      {points.length > 0 && (
        <div>
          <h4 className="font-bold text-brand-navy mb-4 text-xs uppercase tracking-widest opacity-60">{pointsTitle}</h4>
          <ul className="space-y-3">
            {points.map((point, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-600 text-sm">
                <SafeIcon icon={FiIcons.FiCheck} className="text-brand-teal w-4 h-4 shrink-0 mt-0.5" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {extraSections.map((section, idx) => (
        <div key={idx}>
          <h4 className="font-bold text-brand-navy mb-3 text-xs uppercase tracking-widest opacity-60">{section.title}</h4>
          <ul className="grid grid-cols-1 gap-2">
            {section.items.map((item, i) => (
              <li key={i} className="text-gray-600 text-[13px] flex items-center gap-2">
                <span className="w-1 h-1 bg-brand-teal/40 rounded-full"></span>
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}

      {deliverables.length > 0 && (
        <div className="bg-brand-teal/5 p-6 rounded-2xl border border-brand-teal/10">
          <h4 className="font-bold text-brand-navy mb-3 text-xs uppercase tracking-widest">{deliverablesTitle}</h4>
          <ul className="space-y-2">
            {deliverables.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700 text-xs">
                <span className="text-brand-teal font-bold">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>

    <div className="mt-10 pt-8 border-t border-gray-100">
      <div className="mb-8">
        <span className="font-bold text-brand-navy text-[10px] block mb-2 uppercase tracking-widest opacity-60">Final Outcome:</span>
        <p className="text-brand-navy text-lg leading-relaxed font-bold italic">"{outcome}"</p>
      </div>
      <Button 
        to={to} 
        href={href} 
        variant={variant === 'primary' ? 'primary' : 'outline'} 
        fullWidth 
        className="py-5 text-lg font-bold shadow-sm rounded-2xl"
      >
        {linkText}
      </Button>
    </div>
  </div>
);

const Services = () => {
  const discoveryLink = "https://tidycal.com/1x8odpm/ai-discovery-call-1y6rlrd";
  const claritySessionLink = "https://tidycal.com/1x8odpm/ai-clarity-session-1y6rlrd-mpnrkpl";

  return (
    <MainLayout>
      <SEO 
        title="Work With Us | Leading AI with Confidence" 
        description="Helping leaders move from AI uncertainty to confident leadership. Strategic support for mission-led organisations." 
        canonical="/work-with-us" 
      />
      
      {/* Hero Section */}
      <Section bg="cream" className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-brand-navy mb-8 tracking-tight">
            Helping leaders move from AI uncertainty to confident leadership.
          </h1>
          <div className="max-w-2xl mx-auto space-y-6 text-xl text-gray-700 leading-relaxed font-medium">
            <p>Every organisation is at a different stage of its AI journey.</p>
            <p>Our cohesive service ladder is designed to help you discover, review, build confidence, and lead AI in a way that strengthens your mission.</p>
          </div>
        </div>
      </Section>

      {/* Services Grid */}
      <Section bg="white" className="py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Service 1: AI Discovery Call */}
          <ServiceCard 
            title="AI Discovery Call"
            problem="Many organisations know AI is already being used. They simply need someone to help them make sense of where they are before deciding how to lead through it."
            duration="20 mins"
            price="Free"
            description="A free introductory conversation to discuss your organisation, current pressures, and whether a SAFE AI leadership approach is the right fit. This is a discovery session, not a sales call."
            pointsTitle="During the call we cover:"
            points={[
              "Where AI is already showing up in your work",
              "Staff confidence and adoption levels",
              "Immediate concerns for leadership",
              "Potential pathways for support"
            ]}
            outcome="Clarity about whether AI CoLab Studio is the right partner for your organisation."
            href={discoveryLink}
            linkText="Book AI Discovery Call"
          />

          {/* Service 2: AI Leadership Review */}
          <ServiceCard 
            title="AI Leadership Review"
            problem="AI is already showing up across your organisation. The question is whether leadership knows where, why and with what risks."
            duration="90 mins"
            price="£250"
            description="A high-impact strategic working session designed to help mission-led leaders map their current AI reality, identify vulnerabilities, and define immediate priorities."
            pointsTitle="During the review we cover:"
            points={[
              "Map current AI usage across the team",
              "Identify top 3 operational and data risks",
              "Explore immediate high-value opportunities",
              "Define which processes must remain human-led",
              "Prioritise your leadership roadmap"
            ]}
            deliverables={[
              "AI Snapshot Report",
              "Prioritised Action Plan",
              "Risk Assessment Summary"
            ]}
            outcome="A clear leadership action plan for your next 90 days."
            href={claritySessionLink}
            linkText="Book AI Leadership Review"
          />

          {/* Service 3: AI Confidence Training */}
          <div id="training" className="scroll-mt-24">
            <ServiceCard 
              title="AI Confidence Training"
              problem="Giving people access to AI isn't the same as helping them use it confidently. Your team needs capability, not just tools."
              duration="Half-Day / Bespoke"
              price="From £750 (Team)"
              description={[
                "AI tools are becoming part of everyday work. Our AI Confidence Training helps teams understand where AI adds value, when human judgement should lead, and how to use AI consistently in ways that support your mission.",
                "After the training your team will feel confident using AI, understand where people should lead, and share a common understanding of good AI practice."
              ]}
              pointsTitle="After the training your team will:"
              points={[
                "Feel confident using AI in everyday work",
                "Understand where AI adds value and where people lead",
                "Use AI more consistently across the organisation",
                "Save time on routine tasks and produce higher-quality output"
              ]}
              extraSections={[
                {
                  title: "What we'll cover",
                  items: [
                    "Practical prompting techniques",
                    "AI for communications and admin",
                    "Human judgement and decision making",
                    "Responsible everyday AI use",
                    "Team discussions and exercises"
                  ]
                },
                {
                  title: "Ideal for",
                  items: [
                    "Leadership teams & Charity staff",
                    "Community & Arts organisations",
                    "Teams with little or no AI experience"
                  ]
                },
                {
                  title: "Delivery options",
                  items: [
                    "Online or In person",
                    "Half day or Full day sessions",
                    "Tailored to your organisation"
                  ]
                }
              ]}
              outcome="A confident team that uses AI consistently and responsibly."
              href={discoveryLink}
              linkText="Book AI Confidence Training"
            />
          </div>

          {/* Service 4: AI Leadership Sprint */}
          <div id="sprint" className="scroll-mt-24">
            <ServiceCard 
              title="AI Leadership Sprint"
              problem="AI is already shaping your organisation. The question is whether your organisation is shaping AI."
              duration="10-Day Transformation"
              price="From £1,500"
              variant="primary"
              description="A premium, hands-on strategic engagement for leadership teams ready to move from pilot to policy. We build the systems, boundaries, and workflows you need to lead AI with confidence."
              pointsTitle="During the sprint we:"
              points={[
                "Build clear leadership decisions on AI adoption",
                "Implement 2-3 practical workflows to reduce admin",
                "Establish board-ready governance foundations",
                "Document clear organisational guidance for staff",
                "Create a sustainable internal leadership approach"
              ]}
              deliverables={[
                "Completed Acceptable Use Policy",
                "Governance & Risk Framework",
                "Documented Team Workflows",
                "30-day Review Roadmap"
              ]}
              outcome="Sustainable AI leadership and board-ready visibility."
              to="/contact"
              linkText="Enquire About AI Leadership Sprint"
            />
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section bg="white" className="py-24 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-brand-navy mb-8">Ready to lead AI with confidence?</h2>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button to="/contact" variant="primary" className="text-lg px-10 py-5">
              Enquire About Strategic Support
            </Button>
            <Button href={discoveryLink} variant="outline" className="text-lg px-10 py-5">
              Book a Discovery Call
            </Button>
          </div>
        </div>
      </Section>
    </MainLayout>
  );
};

export default Services;